import { formatOtherChoiceAnswer, isOtherChoiceAnswer, type OtherChoiceAnswer } from './choice-answers';
import type { FormField } from '../types';
import { formatQuestionAnswer, questionRequiresOnScreen, validateQuestionStructuredValue, type QuestionStructuredValue } from './question-blocks';
import { getConversationSectionBlocks } from './conversation-sections';
import { parseConversationConfig } from './multimodal-contract';

type CompletionSource = { response_status?: unknown; conversation_progress?: unknown; form_answers?: unknown; form_schema_snapshot?: unknown; conversation_config_snapshot?: unknown };
/** One safe structured projection, separate from original transcript wording. */
export interface StructuredResponseAnswer { fieldId: string; label: string; value: string | number | QuestionStructuredValue | OtherChoiceAnswer; displayValue: string }
export type ResponseCompletion = { multimodal: boolean; submitted: boolean };

/** An authored heading and its frozen question identities for report grounding. */
export interface AuthoredReportSection { id: string; title: string; description?: string; questions: { id: string; label: string }[] }
/** One contiguous group of validated answers, with optional authored metadata. */
export interface StructuredResponseGroup { id: string; title?: string; description?: string; answers: StructuredResponseAnswer[] }

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasReceipt(value: unknown): boolean {
  return isRecord(value) && value.scope === 'full'
    && typeof value.id === 'string' && value.id.length > 0
    && Number.isInteger(value.acceptedRevision) && Number(value.acceptedRevision) > 0
    && typeof value.submittedAt === 'string' && Number.isFinite(Date.parse(value.submittedAt));
}

/** Completion is a receipt, never a guess from turn count, media, or a rating value. */
export function getResponseCompletion(response: CompletionSource): ResponseCompletion {
  const progress = response.conversation_progress;
  const metadata = isRecord(progress) ? progress.multimodal : undefined;
  if (metadata === undefined || metadata === null) {
    return { multimodal: false, submitted: response.response_status === 'completed' };
  }
  if (!isRecord(metadata) || metadata.version !== 1) return { multimodal: true, submitted: false };
  return { multimodal: true, submitted: hasReceipt(metadata.submission) };
}

/** Only an explicit full submission enters response counts and reports. */
export function isFullResponse(response: CompletionSource): boolean {
  return getResponseCompletion(response).submitted;
}

/** Only snapshot-owned, validated answers are reportable; never spread arbitrary stored keys. */
export function getStructuredResponseAnswers(response: CompletionSource): StructuredResponseAnswer[] {
  if (!Array.isArray(response.form_schema_snapshot) || !isRecord(response.form_answers)) return [];
  const answers: StructuredResponseAnswer[] = [];
  for (const field of response.form_schema_snapshot) {
    if (!isRecord(field) || typeof field.id !== 'string' || typeof field.name !== 'string' || typeof field.label !== 'string') continue;
    if (questionRequiresOnScreen(String(field.type))) {
      const snapshotField = field as unknown as FormField;
      const validated = validateQuestionStructuredValue(snapshotField.type === 'date_time' && snapshotField.dateTime
        ? { ...snapshotField, dateTime: { ...snapshotField.dateTime, rules: undefined } } : snapshotField, response.form_answers[field.name]);
      if (!validated.valid) continue;
      const displayValue = formatQuestionAnswer(snapshotField, validated.value);
      if (displayValue !== undefined) answers.push({ fieldId: field.id, label: field.label, value: validated.value, displayValue });
      continue;
    }
    const value = response.form_answers[field.name];
    if (field.type === 'name') {
      // A versioned name object is reported by its full name; a legacy plain string as stored.
      const displayValue = formatQuestionAnswer(field as unknown as FormField, value);
      if (displayValue !== undefined) answers.push({ fieldId: field.id, label: field.label, value: displayValue, displayValue });
      continue;
    }
    if (!['free_form_explore', 'short_text', 'number', 'email', 'phone', 'choice_single', 'choice_multi'].includes(String(field.type))) continue;
    if ((field.type === 'choice_single' || field.type === 'choice_multi') && isOtherChoiceAnswer(value)) {
      const displayValue = formatOtherChoiceAnswer(field, value);
      if (displayValue !== undefined) answers.push({ fieldId: field.id, label: field.label, value, displayValue });
      continue;
    }
    if (!(typeof value === 'string' || (typeof value === 'number' && Number.isFinite(value))
      || (Array.isArray(value) && value.every(item => typeof item === 'string')))) continue;
    const options = Array.isArray(field.options) ? field.options.filter(isRecord) : [];
    const display = (item: string | number): string => {
      const option = options.find(candidate => candidate.value === item && typeof candidate.label === 'string');
      return option ? String(option.label) : String(item);
    };
    answers.push({ fieldId: field.id, label: field.label, value,
      displayValue: Array.isArray(value) ? value.map(display).join(', ') : display(value) });
  }
  return answers;
}

/** Read only the response definition; subsequent campaign edits cannot rewrite history. */
function responseSectionBlocks(response: CompletionSource) {
  const fields = Array.isArray(response.form_schema_snapshot) ? response.form_schema_snapshot.filter(field =>
    isRecord(field) && typeof field.id === 'string' && typeof field.name === 'string' && typeof field.label === 'string' && typeof field.type === 'string') as FormField[] : [];
  const config = parseConversationConfig(response.conversation_config_snapshot);
  return getConversationSectionBlocks(fields, config?.sections);
}

/** Keep legacy answers flat, while authored headings follow the immutable response snapshot. */
export function getStructuredResponseGroups(response: CompletionSource): StructuredResponseGroup[] {
  const answers = new Map(getStructuredResponseAnswers(response).map(answer => [answer.fieldId, answer]));
  return responseSectionBlocks(response).flatMap(block => {
    const grouped = block.fields.flatMap(field => answers.has(field.id) ? [answers.get(field.id)!] : []);
    if (!grouped.length) return [];
    return [{ id: block.kind === 'section' ? block.section.id : `ungrouped-${block.startIndex}`,
      ...(block.kind === 'section' ? { title: block.section.title, ...(block.section.description ? { description: block.section.description } : {}) } : {}), answers: grouped }];
  });
}

/** Supply authored groups as context, never as evidence of an answer or a finding. */
export function getAuthoredReportSections(response: CompletionSource): AuthoredReportSection[] {
  return responseSectionBlocks(response).flatMap(block => block.kind !== 'section' || !block.fields.length ? [] : [{
    id: block.section.id, title: block.section.title, ...(block.section.description ? { description: block.section.description } : {}),
    questions: block.fields.filter(field => field.type !== 'information').map(field => ({ id: field.id, label: field.label })),
  }]);
}

/** Plain-text export preserves original typed/control turns and submission state. */
export function formatResponseTranscript(
  turns: Array<{ speaker: string; text: string; timestamp?: number; modality?: string }>,
  response: CompletionSource,
): string {
  const completion = getResponseCompletion(response);
  const answers = completion.multimodal ? getStructuredResponseAnswers(response) : [];
  const body = turns.map((turn) => {
    const speaker = turn.speaker === 'ai' ? 'Host' : turn.speaker === 'user' ? 'Guest' : 'System';
    const timestamp = turn.timestamp ? new Date(turn.timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    }) : null;
    return `${speaker}${timestamp ? ` [${timestamp}]` : ''}: ${turn.text}`;
  }).join('\n\n');
  const stored = answers.length ? `\n\nStored answers\n${getStructuredResponseGroups(response).map(group => `${group.title ? `${group.title}\n` : ''}${group.answers.map(answer => `${answer.label}: ${answer.displayValue}`).join('\n')}`).join('\n\n')}` : '';
  return completion.multimodal ? `Submission: ${completion.submitted ? 'submitted' : 'not submitted'}\n\n${body}${stored}` : body;
}
